<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useEmitter } from 'dashboard/composables/emitter';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  isAConversationRoute,
  isAInboxViewRoute,
  isNotificationRoute,
} from 'dashboard/helper/routeHelpers';
import { useEventListener } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();

const RECONNECTED_BANNER_TIMEOUT = 2000;
// Uma queda curta da conexão em tempo real (o servidor reiniciando, o Wi-Fi oscilando, a aba voltando
// de segundo plano) não vira aviso: a faixa "Desconectado" só aparece depois de 10 s seguidos sem
// conexão, e se ela volta antes disso nada aparece, nem o "Reconectado".
const DISCONNECTED_BANNER_DELAY = 10000;

const showNotification = ref(false);
const isDisconnected = ref(false);
const isReconnecting = ref(false);
const isReconnected = ref(false);
let reconnectTimeout = null;
let disconnectTimeout = null;
// A faixa "Desconectado" chegou a aparecer nesta queda: só então a volta mostra o "Reconectado".
let offlineBannerShown = false;

const bannerText = computed(() => {
  if (isReconnecting.value) return t('NETWORK.NOTIFICATION.RECONNECTING');
  if (isReconnected.value) return t('NETWORK.NOTIFICATION.RECONNECT_SUCCESS');
  return t('NETWORK.NOTIFICATION.OFFLINE');
});

const iconName = computed(() => (isReconnected.value ? 'wifi' : 'wifi-off'));
const canRefresh = computed(
  () => !isReconnecting.value && !isReconnected.value
);

const refreshPage = () => {
  window.location.reload();
};

const closeNotification = () => {
  showNotification.value = false;
  isReconnected.value = false;
  clearTimeout(reconnectTimeout);
};

const isInAnyOfTheRoutes = routeName => {
  return (
    isAConversationRoute(routeName, true) ||
    isAInboxViewRoute(routeName, true) ||
    isNotificationRoute(routeName, true)
  );
};

const showOfflineBanner = () => {
  disconnectTimeout = null;
  offlineBannerShown = true;
  isReconnecting.value = false;
  isReconnected.value = false;
  clearTimeout(reconnectTimeout);
  showNotification.value = true;
};

// Começa a contar na primeira queda e não recomeça a cada evento repetido: o websocket e o
// `offline` do navegador costumam chegar juntos. Se a faixa já apareceu nesta queda, ela volta na hora.
const scheduleOfflineBanner = () => {
  if (offlineBannerShown) {
    showOfflineBanner();
    return;
  }
  if (disconnectTimeout) return;
  disconnectTimeout = setTimeout(showOfflineBanner, DISCONNECTED_BANNER_DELAY);
};

const cancelOfflineBanner = () => {
  clearTimeout(disconnectTimeout);
  disconnectTimeout = null;
};

const updateWebsocketStatus = () => {
  isDisconnected.value = true;
  scheduleOfflineBanner();
};

const handleReconnectionCompleted = () => {
  isDisconnected.value = false;
  isReconnecting.value = false;
  cancelOfflineBanner();
  if (!offlineBannerShown) return;
  offlineBannerShown = false;
  isReconnected.value = true;
  showNotification.value = true;
  clearTimeout(reconnectTimeout);
  reconnectTimeout = setTimeout(closeNotification, RECONNECTED_BANNER_TIMEOUT);
};

const handleReconnecting = () => {
  cancelOfflineBanner();
  if (!isInAnyOfTheRoutes(route.name)) {
    handleReconnectionCompleted();
    return;
  }
  // A conexão voltou e a tela recarrega as conversas. Se a faixa não chegou a aparecer, segue sem nada.
  if (!offlineBannerShown) return;
  isReconnecting.value = true;
  isReconnected.value = false;
  showNotification.value = true;
};

const updateOnlineStatus = event => {
  // Case: Websocket is not disconnected
  // If the app goes offline, wait DISCONNECTED_BANNER_DELAY before showing the notification
  // If the app goes online, cancel the wait or close the notification

  // Case: Websocket is disconnected
  // If the app goes offline, wait DISCONNECTED_BANNER_DELAY before showing the notification
  // If the app goes online but the websocket is disconnected, keep waiting (or keep the notification)
  // If the app goes online and the websocket is not disconnected, close the notification

  if (event.type === 'offline') {
    scheduleOfflineBanner();
  } else if (event.type === 'online' && !isDisconnected.value) {
    handleReconnectionCompleted();
  }
};

// Página aberta já sem rede conta como queda desde o início.
if (!navigator.onLine) scheduleOfflineBanner();

useEventListener('online', updateOnlineStatus);
useEventListener('offline', updateOnlineStatus);
useEmitter(BUS_EVENTS.WEBSOCKET_DISCONNECT, updateWebsocketStatus);
useEmitter(
  BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED,
  handleReconnectionCompleted
);
useEmitter(BUS_EVENTS.WEBSOCKET_RECONNECT, handleReconnecting);

onBeforeUnmount(() => {
  clearTimeout(reconnectTimeout);
  cancelOfflineBanner();
});
</script>

<template>
  <transition name="network-notification-fade" tag="div">
    <div v-show="showNotification" class="fixed z-50 top-2 left-2 group">
      <div
        class="relative flex items-center justify-between w-full px-2 py-1 bg-n-amber-4 dark:bg-n-amber-8 rounded-lg shadow-lg"
      >
        <fluent-icon :icon="iconName" class="text-n-amber-12" size="18" />
        <span class="px-2 text-xs font-medium tracking-wide text-n-amber-12">
          {{ bannerText }}
        </span>
        <Button
          v-if="canRefresh"
          ghost
          sm
          amber
          icon="i-lucide-refresh-ccw"
          :title="$t('NETWORK.BUTTON.REFRESH')"
          class="!text-n-amber-12 dark:!text-n-amber-9"
          @click="refreshPage"
        />

        <Button
          ghost
          sm
          amber
          icon="i-lucide-x"
          class="!text-n-amber-12 dark:!text-n-amber-9"
          @click="closeNotification"
        />
      </div>
    </div>
  </transition>
</template>
